import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { createAgentAssignment, getAgentAssignment, updateAgentAssignment, clearAgentAssignment } from '../../actions/agentAssignment';
import { getServiceableAreas } from '../../actions/serviceableArea';
import { getSectors } from '../../actions/sector';
import { getAgents } from '../../actions/agent';
import { ArrowLeft, Save, Users, Map, Activity, FileText, UserCheck, Globe, Layers, MessageSquare } from 'lucide-react';

const initialState = {
    agent: '',
    sectors: [],
    isFloating: false,
    serviceableArea: '',
    isActive: true,
    notes: ''
};

const AgentSectorAssignment = ({
    createAgentAssignment,
    getAgentAssignment,
    updateAgentAssignment,
    clearAgentAssignment,
    getServiceableAreas,
    getSectors,
    getAgents,
    agentAssignment,
    serviceableAreas,
    sectors,
    agents,
    loading
}) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState(initialState);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [filteredSectors, setFilteredSectors] = useState([]);

    const isNewAssignment = id === 'new';

    useEffect(() => {
        document.title = isNewAssignment ? 'Create Agent Sector Assignment' : 'Edit Agent Sector Assignment';
        
        // Load dropdown data
        getServiceableAreas();
        getSectors();
        getAgents();
        
        if (!isNewAssignment && (!agentAssignment || agentAssignment._id !== id)) {
            getAgentAssignment(id);
        } else if (!isNewAssignment && agentAssignment && agentAssignment._id === id) {
            setFormData({
                ...initialState,
                ...agentAssignment,
                agent: agentAssignment.agent?._id || agentAssignment.agent,
                serviceableArea: agentAssignment.serviceableArea?._id || agentAssignment.serviceableArea,
                sectors: agentAssignment.sectors?.map(sector => sector._id || sector) || []
            });
        } else if (isNewAssignment) {
            clearAgentAssignment();
            setFormData(initialState);
        }
    }, [id, isNewAssignment, agentAssignment, getAgentAssignment, clearAgentAssignment, getServiceableAreas, getSectors, getAgents]);

    // Filter sectors based on selected serviceable area
    useEffect(() => {
        if (formData.serviceableArea && sectors) {
            const filtered = sectors.filter(sector => 
                sector.serviceableArea?._id === formData.serviceableArea ||
                sector.serviceableArea === formData.serviceableArea
            );
            setFilteredSectors(filtered);
            
            // Clear selected sectors if they don't belong to the new serviceable area
            const validSectors = formData.sectors.filter(sectorId =>
                filtered.some(sector => sector._id === sectorId)
            );
            if (validSectors.length !== formData.sectors.length) {
                setFormData(prev => ({ ...prev, sectors: validSectors }));
            }
        } else {
            setFilteredSectors([]);
        }
    }, [formData.serviceableArea, sectors, formData.sectors]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }

        if (name === 'isFloating' && checked) {
            // If floating is enabled, clear sectors selection
            setFormData(prev => ({
                ...prev,
                [name]: checked,
                sectors: []
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    const handleSectorChange = (e) => {
        const sectorId = e.target.value;
        const isChecked = e.target.checked;
        
        setFormData(prev => ({
            ...prev,
            sectors: isChecked 
                ? [...prev.sectors, sectorId]
                : prev.sectors.filter(id => id !== sectorId)
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.agent) {
            newErrors.agent = 'Agent is required';
        }

        if (!formData.serviceableArea) {
            newErrors.serviceableArea = 'Serviceable area is required';
        }

        if (!formData.isFloating && formData.sectors.length === 0) {
            newErrors.sectors = 'At least one sector must be selected for fixed assignments';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        try {
            setIsSubmitting(true);
            
            const submissionData = {
                ...formData,
                sectors: formData.isFloating ? [] : formData.sectors
            };
            
            if (isNewAssignment) {
                await createAgentAssignment(submissionData);
                navigate('/inventory/v1.2/agent-sector-assignments');
            } else {
                await updateAgentAssignment(agentAssignment._id, submissionData);
            }
        } catch (error) {
            console.error('Error submitting form:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading && !isNewAssignment) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading assignment details...</p>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <Link
                                className="btn btn-outline-primary d-flex align-items-center"
                                to="/inventory/v1.2/agent-sector-assignments"
                            >
                                <ArrowLeft size={16} className="me-2" />
                                Back to Agent Sector Assignments
                            </Link>
                            <h2 className="text-primary mb-0 fw-bold">
                                {isNewAssignment ? 'Create Agent Sector Assignment' : 'Edit Sector Assignment'}
                            </h2>
                            <div></div>
                        </div>
                    </div>
                </div>

                <form onSubmit={onSubmit}>
                    <div className="row">
                        {/* Main Form */}
                        <div className="col-lg-8">
                            {/* Basic Assignment Info */}
                            <div className="card shadow-sm mb-4">
                                <div className="card-header bg-primary text-white">
                                    <h5 className="mb-0 d-flex align-items-center">
                                        <FileText size={20} className="me-2" />
                                        Assignment Details
                                    </h5>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Agent *</label>
                                            <select
                                                name="agent"
                                                value={formData.agent}
                                                onChange={handleChange}
                                                className={`form-select ${errors.agent ? 'is-invalid' : ''}`}
                                            >
                                                <option value="">Select an agent</option>
                                                {agents?.map(agent => (
                                                    <option key={agent._id} value={agent._id}>
                                                        {agent.name} {agent.phone && `(${agent.phone})`}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.agent && <div className="invalid-feedback">{errors.agent}</div>}
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Serviceable Area *</label>
                                            <select
                                                name="serviceableArea"
                                                value={formData.serviceableArea}
                                                onChange={handleChange}
                                                className={`form-select ${errors.serviceableArea ? 'is-invalid' : ''}`}
                                            >
                                                <option value="">Select serviceable area</option>
                                                {serviceableAreas?.map(area => (
                                                    <option key={area._id} value={area._id}>
                                                        {area.name} ({area.city}, {area.state})
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.serviceableArea && <div className="invalid-feedback">{errors.serviceableArea}</div>}
                                        </div>

                                        <div className="col-12 mb-3">
                                            <div className="form-check form-switch">
                                                <input
                                                    type="checkbox"
                                                    name="isFloating"
                                                    checked={formData.isFloating}
                                                    onChange={handleChange}
                                                    className="form-check-input"
                                                    id="isFloating"
                                                />
                                                <label className="form-check-label" htmlFor="isFloating">
                                                    <Globe size={16} className="me-1" />
                                                    Floating Agent (can operate across all sectors)
                                                </label>
                                            </div>
                                            <small className="text-muted">
                                                Floating agents are not restricted to specific sectors and can work anywhere within the serviceable area.
                                            </small>
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Status</label>
                                            <div className="form-check form-switch">
                                                <input
                                                    type="checkbox"
                                                    name="isActive"
                                                    checked={formData.isActive}
                                                    onChange={handleChange}
                                                    className="form-check-input"
                                                    id="isActive"
                                                />
                                                <label className="form-check-label" htmlFor="isActive">
                                                    <Activity size={16} className="me-1" />
                                                    {formData.isActive ? 'Active' : 'Inactive'}
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Sector Selection */}
                            {!formData.isFloating && (
                                <div className="card shadow-sm mb-4">
                                    <div className="card-header bg-info text-white">
                                        <h5 className="mb-0 d-flex align-items-center">
                                            <Layers size={20} className="me-2" />
                                            Sector Assignment
                                        </h5>
                                    </div>
                                    <div className="card-body">
                                        {formData.serviceableArea ? (
                                            <>
                                                {filteredSectors.length > 0 ? (
                                                    <>
                                                        <div className="alert alert-info">
                                                            <strong>Instructions:</strong> Select the sectors where this agent can operate. 
                                                            The agent will only have access to selected sectors within the chosen serviceable area.
                                                        </div>
                                                        <div className="row">
                                                            {filteredSectors.map(sector => (
                                                                <div key={sector._id} className="col-md-6 mb-2">
                                                                    <div className="form-check">
                                                                        <input
                                                                            type="checkbox"
                                                                            value={sector._id}
                                                                            checked={formData.sectors.includes(sector._id)}
                                                                            onChange={handleSectorChange}
                                                                            className="form-check-input"
                                                                            id={`sector-${sector._id}`}
                                                                        />
                                                                        <label className="form-check-label" htmlFor={`sector-${sector._id}`}>
                                                                            <strong>{sector.name}</strong>
                                                                            {sector.description && (
                                                                                <small className="text-muted d-block">
                                                                                    {sector.description.length > 50 
                                                                                        ? `${sector.description.substring(0, 50)}...`
                                                                                        : sector.description
                                                                                    }
                                                                                </small>
                                                                            )}
                                                                        </label>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                        {errors.sectors && (
                                                            <div className="text-danger mt-2">{errors.sectors}</div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <div className="alert alert-warning">
                                                        <Layers size={16} className="me-2" />
                                                        No sectors found for the selected serviceable area. 
                                                        Please create sectors first or select a different serviceable area.
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="alert alert-secondary">
                                                <Map size={16} className="me-2" />
                                                Please select a serviceable area first to see available sectors.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Notes */}
                            <div className="card shadow-sm mb-4">
                                <div className="card-header bg-warning text-dark">
                                    <h5 className="mb-0 d-flex align-items-center">
                                        <MessageSquare size={20} className="me-2" />
                                        Additional Notes
                                    </h5>
                                </div>
                                <div className="card-body">
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Notes</label>
                                        <textarea
                                            name="notes"
                                            value={formData.notes}
                                            onChange={handleChange}
                                            className="form-control"
                                            placeholder="Add any special instructions or notes about this assignment..."
                                            rows="4"
                                        />
                                        <div className="form-text">
                                            Optional notes about special conditions, restrictions, or instructions for this assignment.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Summary Panel */}
                        <div className="col-lg-4">
                            <div className="card shadow-sm sticky-top">
                                <div className="card-header bg-light">
                                    <h5 className="mb-0">Assignment Summary</h5>
                                </div>
                                <div className="card-body">
                                    <div className="mb-3">
                                        <strong>Agent:</strong>
                                        <div className="text-muted">
                                            {formData.agent ? (
                                                agents?.find(agent => agent._id === formData.agent)?.name
                                            ) : (
                                                <em>Not selected</em>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="mb-3">
                                        <strong>Serviceable Area:</strong>
                                        <div className="text-muted">
                                            {formData.serviceableArea ? (
                                                serviceableAreas?.find(area => area._id === formData.serviceableArea)?.name
                                            ) : (
                                                <em>Not selected</em>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="mb-3">
                                        <strong>Assignment Type:</strong>
                                        <div>
                                            <span className={`badge bg-${formData.isFloating ? 'info' : 'warning'}`}>
                                                {formData.isFloating ? (
                                                    <>
                                                        <Globe size={12} className="me-1" />
                                                        Floating
                                                    </>
                                                ) : (
                                                    <>
                                                        <Map size={12} className="me-1" />
                                                        Fixed
                                                    </>
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <div className="mb-3">
                                        <strong>Status:</strong>
                                        <div>
                                            <span className={`badge bg-${formData.isActive ? 'success' : 'secondary'}`}>
                                                {formData.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <div className="mb-3">
                                        <strong>Sectors:</strong>
                                        <div className="text-muted">
                                            {formData.isFloating ? (
                                                <em>All sectors (floating)</em>
                                            ) : formData.sectors.length > 0 ? (
                                                <div>
                                                    <span className="badge bg-secondary">
                                                        {formData.sectors.length} sector{formData.sectors.length > 1 ? 's' : ''}
                                                    </span>
                                                    <div className="mt-1">
                                                        {formData.sectors.slice(0, 3).map(sectorId => {
                                                            const sector = filteredSectors.find(s => s._id === sectorId);
                                                            return sector ? (
                                                                <small key={sectorId} className="text-muted d-block">
                                                                    • {sector.name}
                                                                </small>
                                                            ) : null;
                                                        })}
                                                        {formData.sectors.length > 3 && (
                                                            <small className="text-muted">
                                                                +{formData.sectors.length - 3} more
                                                            </small>
                                                        )}
                                                    </div>
                                                </div>
                                            ) : (
                                                <em>No sectors selected</em>
                                            )}
                                        </div>
                                    </div>

                                    {formData.notes && (
                                        <div className="mb-3">
                                            <strong>Notes:</strong>
                                            <div className="text-muted small">
                                                {formData.notes.length > 100 
                                                    ? `${formData.notes.substring(0, 100)}...`
                                                    : formData.notes
                                                }
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="row">
                        <div className="col-12">
                            <div className="d-flex justify-content-end gap-2">
                                <Link
                                    to="/inventory/v1.2/agent-sector-assignments"
                                    className="btn btn-outline-secondary"
                                >
                                    Cancel
                                </Link>
                                <button
                                    type="submit"
                                    className="btn btn-primary btn-lg"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <div className="spinner-border spinner-border-sm me-2" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            {isNewAssignment ? 'Creating...' : 'Updating...'}
                                        </>
                                    ) : (
                                        <>
                                            <Save size={16} className="me-2" />
                                            {isNewAssignment ? 'Create Sector Assignment' : 'Update Sector Assignment'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </Fragment>
    );
};

AgentSectorAssignment.propTypes = {
    createAgentAssignment: PropTypes.func.isRequired,
    getAgentAssignment: PropTypes.func.isRequired,
    updateAgentAssignment: PropTypes.func.isRequired,
    clearAgentAssignment: PropTypes.func.isRequired,
    getServiceableAreas: PropTypes.func.isRequired,
    getSectors: PropTypes.func.isRequired,
    getAgents: PropTypes.func.isRequired,
    agentAssignment: PropTypes.object,
    serviceableAreas: PropTypes.array,
    sectors: PropTypes.array,
    agents: PropTypes.array,
    loading: PropTypes.bool.isRequired
};

const mapStateToProps = (state) => ({
    agentAssignment: state.agentAssignment.agentAssignment,
    serviceableAreas: state.serviceableArea.serviceableAreas,
    sectors: state.sector.sectors,
    agents: state.agent.agents,
    loading: state.agentAssignment.loading
});

export default connect(mapStateToProps, {
    createAgentAssignment,
    getAgentAssignment,
    updateAgentAssignment,
    clearAgentAssignment,
    getServiceableAreas,
    getSectors,
    getAgents
})(AgentSectorAssignment);
